import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectParameterForm from "../Forms/SelectParameterForm";
import BaseMultistepModal from "./BaseMultistepModal";
import OrganizationSubscriptionsAPI from "../../API/organizationSubscriptions.api";
import DisplayUnitForm from "../DisplayUnitSteps/DisplayUnitForm";
import { SuccessAlert } from "../../utils/Alerts";

const DisplayUnitModal = ({
    isVisible,
    setIsVisible,
    selectedProduct,
    isLoading,
    selectedProductDetails,
}) => {
    const [productParameters, setProductParameters] = useState({});
    const [selectedBundleProduct, setSelectedBundleProduct] = useState(null);
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [availableUnits, setAvailableUnits] = useState([]);
    const [selectedDisplayUnit, setSelectedDisplayUnit] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const multiStepModalRef = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isVisible) {
            resetForm();
        }
    }, [isVisible]);

    const resetForm = () => {
        setSelectedBundleProduct(null);
        setSelectedParameter(null);
        setSelectedDisplayUnit(null);
        setAvailableUnits([]);
    };

    const submitDisplayUnit = async () => {
        const isBundle = selectedProductDetails.isBundle;
        const subscriptionMaterialId = isBundle
            ? selectedBundleProduct.subscriptionMaterialId
            : selectedProductDetails.subscriptionMaterialId;
        setIsSubmitting(true);
        const result = await OrganizationSubscriptionsAPI.selectDisplayUnit({
            modelParameterProductVersionId: selectedParameter?.value,
            organizationSubscriptionId: selectedProduct?.id,
            unitConversionId: selectedDisplayUnit?.value,
            subscriptionMaterialId,
        });

        if (result?.modified) {
            SuccessAlert(t("displayUnitUpdateSuccessMessage"));
        }
        setIsSubmitting(false);
    };

    const getSteps = () => {
        return [
            {
                name: t("selectParameter"),
                content: () => {
                    return (
                        <div className="px-4">
                            <SelectParameterForm
                                isBundle={selectedProductDetails.isBundle}
                                bundleProducts={
                                    selectedProductDetails.bundleProducts
                                }
                                parameters={selectedProductDetails.parameters}
                                selectedBundleProduct={selectedBundleProduct}
                                setSelectedBundleProduct={
                                    setSelectedBundleProduct
                                }
                                selectedParameter={selectedParameter}
                                setSelectedParameter={setSelectedParameter}
                                productParameters={productParameters}
                                setProductParameters={setProductParameters}
                            />
                        </div>
                    );
                },
                action: {
                    name: t("next"),
                    disabled: !selectedParameter,
                    submitAction: () => multiStepModalRef.current.nextPage(),
                },
            },
            {
                name: t("displayUnit"),
                content: () => {
                    return (
                        <div className="p-3">
                            <DisplayUnitForm
                                modelParameterProductVersionId={
                                    selectedParameter?.value
                                }
                                organizationSubscriptionId={selectedProduct?.id}
                                selectedDisplayUnit={selectedDisplayUnit}
                                setSelectedDisplayUnit={setSelectedDisplayUnit}
                                subscriptionMaterialId={
                                    selectedProductDetails.isBundle
                                        ? selectedBundleProduct?.subscriptionMaterialId
                                        : selectedProductDetails.subscriptionMaterialId
                                }
                                availableUnits={availableUnits}
                                setAvailableUnits={setAvailableUnits}
                            />
                        </div>
                    );
                },
                action: {
                    name: isSubmitting ? t("requesting") : t("save"),
                    disabled: isSubmitting || availableUnits.length === 0,
                    isSubmitting,
                    submitAction: () => {
                        submitDisplayUnit().then(() => setIsVisible(false));
                    },
                },
            },
        ];
    };

    return (
        <BaseMultistepModal
            ref={multiStepModalRef}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            title={
                <>
                    {`${t("displayUnitFor")} `}
                    <strong>{selectedProduct?.productName}</strong>
                </>
            }
            isMedium={true}
            steps={getSteps()}
            isLoading={isLoading}
        />
    );
};

export default DisplayUnitModal;
